/**
 * RULL TIL DET FØRSTE FELTET SOM STOPPER DEG (QA13 runde 3, blokk 3 — rest 31a).
 *
 * =================================================================================================
 * Atle, runde 2 punkt 31a: veiviseren nektet uten å si hva som manglet. To tredeler ble rettet
 * underveis — knappen sier «Mangler navn (steg 1 av 5)» framfor å bare være grå, og `Field` har
 * fått stjerna på det som må fylles ut. Denne er den siste: at man havner DER feilen er.
 *
 * Et skjema som er lengre enn skjermen kan sette en feilmelding på et felt ingen ser. Da er
 * beskjeden teknisk sett gitt og praktisk talt ikke gitt, og leseren trykker på knappen en gang
 * til fordi ingenting synlig skjedde.
 *
 * =================================================================================================
 * DEN LETER I DOM-EN FRAMFOR Å FÅ FEILENE INN SOM EN LISTE, og det er et valg.
 *
 * En liste over feltnavn måtte vært holdt i takt med rekkefølgen på skjermen — og den rekkefølgen
 * er det bare DOM-en som vet, fordi den avhenger av hvilket steg som er åpent, hvilke seksjoner som
 * er foldet ut, og hvilke felt som i det hele tatt tegnes for denne rollen. `document.querySelector`
 * svarer på «det første, slik siden faktisk ser ut», som er nøyaktig spørsmålet.
 *
 * `data-field-invalid` settes av `Field` på den YTRE boksen: ruller man til selve input-en, havner
 * feilmeldingen under den rett utenfor skjermkanten, og da er man like langt.
 *
 * =================================================================================================
 * FOKUS I TILLEGG TIL RULLING, og fokus SIST.
 *
 * Rullingen er for den som ser; fokus er for den som navigerer med tastatur eller skjermleser, og
 * flytter samtidig markøren dit man skal skrive. `preventScroll` er satt fordi nettleseren ellers
 * gjør sitt EGET hopp til kontrollen — uten margen `scrollIntoView` legger inn — og de to
 * bevegelsene rykker i hverandre.
 *
 * Kalles ETTER at feilene er satt i tilstanden, altså i samme sving som valideringen feilet. Finner
 * den ingenting, gjør den ingenting: et skjema uten synlige feil skal ikke rykke.
 */

/** Det ytre feltet som bærer en feil akkurat nå, slik siden ser ut. */
function firstInvalidField(root: ParentNode): HTMLElement | null {
  const node = root.querySelector('[data-field-invalid="true"]');
  return node instanceof HTMLElement ? node : null;
}

/** Kontrollen inni det, hvis den kan få fokus. */
function focusableIn(field: HTMLElement): HTMLElement | null {
  const node = field.querySelector<HTMLElement>(
    'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])',
  );
  return node ?? null;
}

/**
 * Ruller det første feltet med en feil i syne og setter fokus i det.
 *
 * Svarer `true` når den fant noe, så den som kaller kan la være å gjøre noe annet i stedet — og så
 * en test kan måle at det ikke fantes noe å rulle til uten å lese DOM-en selv.
 */
export function scrollToFirstError(root: ParentNode = document): boolean {
  const field = firstInvalidField(root);
  if (field === null) return false;

  /*
   * `block: 'center'` og ikke `'start'`: et felt som lander helt øverst står ofte under en
   * klebrig topplinje eller en fanerad, og «i syne» er da ikke sant. Midten koster litt plass og
   * er riktig uansett hvor høy toppen er.
   *
   * `scrollIntoView` finnes ikke i jsdom — det er en layout-funksjon, og jsdom regner ingen
   * layout. Vakten er derfor ikke defensiv programmering mot en ukjent nettleser, men det som
   * lar denne kjøre i en test i det hele tatt.
   */
  if (typeof field.scrollIntoView === 'function') {
    field.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
  focusableIn(field)?.focus({ preventScroll: true });
  return true;
}
